import React, { useRef, useState, useEffect } from "react";

const InsightSection = ({ section }) => {
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const keyServices2 = section || [];

  // ✅ Scroll function (card-based)
  const scroll = (direction) => {
    if (!scrollRef.current) return;

    const card = scrollRef.current.querySelector("div");
    if (!card) return;

    const cardWidth = card.offsetWidth + 16;

    scrollRef.current.scrollBy({
      left: direction === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };

  // ✅ Check scroll position
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollLeft = el.scrollLeft;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < maxScrollLeft - 5);
  };

  // ✅ Attach listeners
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll(); // initial

    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  return (
    <section className="relative w-full pt-24 pb-10 bg-white overflow-visible">
      {/* Background Text */}
      <div
        className="absolute top-0 left-0 w-full h-full flex items-start pointer-events-none select-none md:px-6"
        style={{ paddingTop: "2rem" }}
      >
        <h1 className="text-[25px] md:text-md font-black text-[#1325730F] leading-none uppercase whitespace-nowrap">
          Industry Perspective
        </h1>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto md:px-6 text-center">
        <div className="flex justify-center md:justify-end md:pr-20 mb-4">
          <img
            src="/assets/avexaOneCare/quotesIcon.svg"
            alt="quotesIcon"
            className="w-8 md:w-10 opacity-80"
          />
        </div>

        <h2 className=" font-normal my-8 leading-tight">
          Healthcare’s next transformation is unification
        </h2>

        <p className="  leading-relaxed md:max-w-4xl mx-auto mb-7 md:mb-1">
          Healthcare has digitized—but remains fragmented across systems,
          workflows, and data. The next evolution is not about adding more
          tools, but about connecting them into a unified intelligence layer
          that enables continuous care, real-time decisions, and better outcomes
          at scale.
        </p>
      </div>

      <div className="relative w-full">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 flex items-end pointer-events-none">
          <img
            src="/assets/avexaOneCare/InsightSectionBg.svg"
            alt="Background"
            className="w-full h-2/3 object-cover opacity-80"
          />
        </div>

        <div className="relative z-10 mx-auto px-6">
          {/* ✅ Arrows */}
          <div className="flex justify-center md:justify-end gap-2 mb-12 md:mb-6">
            {/* LEFT */}
            <button
              onClick={canScrollLeft ? () => scroll("left") : undefined}
              disabled={!canScrollLeft}
              className={`p-3 md:p-2 rounded-full border shadow-sm transition active:scale-95
                ${
                  canScrollLeft
                    ? "border-slate-200 bg-white hover:bg-slate-50 "
                    : "bg-gray-200 text-gray-400 cursor-not-allowed border-gray-200"
                }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* RIGHT */}
            <button
              onClick={canScrollRight ? () => scroll("right") : undefined}
              disabled={!canScrollRight}
              className={`p-3 md:p-2 rounded-full border shadow-sm transition active:scale-95
                ${
                  canScrollRight
                    ? "border-slate-200 bg-white hover:bg-slate-50 "
                    : "bg-gray-200 text-gray-400 cursor-not-allowed border-gray-200"
                }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* ✅ Scroll Container */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory md:pb-32"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {keyServices2.map((item) => (
              <div
                key={item.id}
                className="flex-shrink-0 w-full md:w-[49%] lg:w-[33.333%] xl:w-[24%] bg-[#F4F7FF]/90 backdrop-blur-sm p-8 rounded-xl shadow-sm border border-white snap-start mb-14 md:mb-0"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1E2A5A] text-white  font-bold">
                    {item.id}
                  </span>
                  <span className=" font-bold tracking-widest uppercase">
                    {item.tag}
                  </span>
                </div>

                <h3 className=" font-bold mb-4 leading-tight ">{item.title}</h3>

                <p className=" leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InsightSection;
